import React, { useEffect, useState } from 'react';
import Select, { ValueType } from 'react-select';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { InputText } from '../../../../shared/components/core-ui/input-text/input-text.component';
import { TeamUiModel } from '../../../../shared/ui-models/team.ui-model';
import { AccessControlFilterConfigModel } from '../../../../shared/models/team.model';

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';

interface Option {
  value: string;
  label: string;
}

export interface TeamFormValue {
  name: string;
  accessControl: { module: string; enforcedFilters: { key: string; value: string[] }[] }[];
}

interface TeamFormModalProps {
  show: boolean;
  team?: TeamUiModel;
  /** The allow-list the server validates against. Pickers are built from it. */
  filterConfig: AccessControlFilterConfigModel;
  onClose: () => void;
  onSubmit: (value: TeamFormValue) => void;
}

export const TeamFormModal: React.FC<TeamFormModalProps> = ({
  show,
  team,
  filterConfig,
  onClose,
  onSubmit
}) => {
  const isEdit = Boolean(team);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  const leadFilters = filterConfig.leads ?? {};
  const filterKeys = Object.keys(leadFilters);

  useEffect(() => {
    setName(team?.name ?? '');
    const grant = team?.grants.find((item) => item.module === 'leads');
    const next: Record<string, string[]> = {};
    (grant?.filters ?? []).forEach((filter) => {
      next[filter.key] = filter.values;
    });
    setSelected(next);
  }, [team, show]);

  const setValues = (key: string, values: string[]) => {
    const updated = { ...selected };
    if (values.length) updated[key] = values;
    else delete updated[key];
    setSelected(updated);
  };

  const hasAnyFilter = Object.keys(selected).length > 0;

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[85vh] lg:w-[620px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          {isEdit ? 'Edit team' : 'Create team'}
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-4">
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            disabled={name.trim().length === 0}
            tracking={{
              event: 'record_saved',
              entity: 'team',
              action: isEdit ? 'update' : 'create',
              outcome: 'success'
            }}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                accessControl: [
                  {
                    module: 'leads',
                    enforcedFilters: Object.keys(selected).map((key: string) => ({
                      key,
                      value: selected[key]
                    }))
                  }
                ]
              })
            }
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 px-2 py-2">
        <div>
          <label className={LABEL}>Name</label>
          <InputText
            data={{ value: name, placeholder: 'e.g. Acquisition — Cairo' }}
            config={{ required: true }}
            eventHandlers={{ onChangeHandler: (value: string) => setName(value) }}
          />
        </div>

        <div>
          <h3 className="font-gilroy-bold text-sm text-neutral-500">Lead access</h3>
          <p className="mb-3 text-xs text-neutral-400">
            Leave everything empty and the team gets an unconditional org-wide browse of leads. Pick
            values and the browse is narrowed to them. This grant widens what managers see beyond
            their own downline — it never narrows an agent&apos;s own book.
          </p>

          {filterKeys.length === 0 ? (
            <p className="text-xs text-neutral-400">No filter keys configured.</p>
          ) : (
            filterKeys.map((key: string) => {
              const options: Option[] = (leadFilters[key].allowedValues ?? []).map(
                (value: string) => ({ value, label: value })
              );
              return (
                <div key={key} className="mb-3">
                  <label className={LABEL}>
                    {key}
                    <span className="ml-2 font-normal text-neutral-300">
                      {leadFilters[key].description}
                    </span>
                  </label>
                  <Select
                    isMulti
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectMenuPortalStyles}
                    classNamePrefix="react-select"
                    options={options}
                    placeholder="Any"
                    value={options.filter(
                      (option: Option) => (selected[key] ?? []).indexOf(option.value) >= 0
                    )}
                    onChange={(value: ValueType<Option, true>) =>
                      setValues(
                        key,
                        ((value as Option[]) || []).map((option) => option.value)
                      )
                    }
                  />
                </div>
              );
            })
          )}

          {!hasAnyFilter ? (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This team will be able to browse <strong>every</strong> lead.
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
